import { useState, useEffect } from 'react';

/*
NOTE: Intermediary states are batched in tests, so `isUpdateDetected: true` 
immediately triggering a `setIsUpdateDetected(false)` state change was not 
testable. So `isUpdateDetected` must be manually reset with `updateHandled()`.
*/

const DEFAULT_INTERVAL = 1000;
const NOT_POLLING = 0;

type UsePollForUpdates<T> = {
  isUpdateDetected: boolean;
  pollForUpdates: () => void;
  pollingController: number;
  setApiResult: React.Dispatch<React.SetStateAction<T | undefined>>;
  setPollingInterval: React.Dispatch<React.SetStateAction<number>>;
  updateHandled: () => void;
};

const usePollForUpdates = <T>(): UsePollForUpdates<T> => {
  const [cachedComparableResult, setCachedComparableResult] = useState<
    string
  >();
  const [isPollingForUpdates, setIsPollingForUpdates] = useState<boolean>(
    false
  );
  const [pollingAtInterval, setPollingInterval] = useState<number>(
    DEFAULT_INTERVAL
  );
  const [pollingController, setPollingController] = useState<number>(
    NOT_POLLING
  );
  const [apiResult, setApiResult] = useState<T>();
  const [apiComparableResult, setApiComparableResult] = useState<string>();
  const [isUpdateDetected, setIsUpdateDetected] = useState<boolean>(false);

  const pollForUpdates = () => {
    setIsPollingForUpdates(true);
  };

  const updateHandled = () => {
    setIsUpdateDetected(false);
  };

  useEffect(() => {
    const interval = isPollingForUpdates ? pollingAtInterval : NOT_POLLING;
    setPollingController(interval);
  }, [isPollingForUpdates, pollingAtInterval]);

  useEffect(() => {
    if (apiResult) {
      const stringified = JSON.stringify(apiResult);
      setApiComparableResult(stringified);
    }
  }, [apiResult]);

  useEffect(() => {
    if (cachedComparableResult !== apiComparableResult) {
      setCachedComparableResult(apiComparableResult);

      if (isPollingForUpdates) {
        setIsUpdateDetected(true);
        setIsPollingForUpdates(false);
      }
    }
  }, [cachedComparableResult, apiComparableResult, isPollingForUpdates]);

  return {
    isUpdateDetected,
    pollForUpdates,
    pollingController,
    setApiResult,
    setPollingInterval,
    updateHandled,
  };
};

export { usePollForUpdates };
